/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/pagingGroupsApi.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    API calls for Paging Groups (fetch list, fetch by ID, create, update, delete).
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { PagingGroup, PagingGroupFormState } from '../types';

export interface FetchPagingGroupsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface FetchPagingGroupsResponse {
  data: PagingGroup[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: boolean;
}

export const fetchPagingGroups = async (
  params: FetchPagingGroupsParams
): Promise<FetchPagingGroupsResponse> => {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(buildApiUrl(`/api/telephony/paging-groups?${queryParams}`), {
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch paging groups');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch paging groups');
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0,
    },
    success: true,
  };
};

export const fetchPagingGroupById = async (id: string): Promise<PagingGroup> => {
  const response = await fetch(buildApiUrl(`/api/telephony/paging-groups/${id}`), {
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch paging group');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch paging group');
  }

  return result.data;
};

export const createPagingGroup = async (
  payload: Partial<PagingGroupFormState> & { members?: string[] }
): Promise<{ id: number; uuid: string }> => {
  const response = await fetch(buildApiUrl('/api/telephony/paging-groups'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create paging group');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create paging group');
  }

  return { id: result.data.id, uuid: result.data.uuid };
};

export const updatePagingGroup = async (
  id: string,
  payload: Partial<PagingGroupFormState> & { members?: string[] }
): Promise<{ id: number; uuid: string }> => {
  const response = await fetch(buildApiUrl(`/api/telephony/paging-groups/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update paging group');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update paging group');
  }

  return { id: result.data.id, uuid: result.data.uuid };
};

export const deletePagingGroup = async (id: string): Promise<void> => {
  const response = await fetch(buildApiUrl(`/api/telephony/paging-groups/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to delete paging group');
  }
};

